import { namespaceToPackageMappings } from '../trace/callgraph/packagistPopularNamespaces'
import { sortXhprofGenericDataObject, XhprofCall } from './XhprofProfiler'

export interface XhprofReport {
    data: XhprofFlatInfoData
    layerReport: string[]
    namespaces: XhprofNamespace[]
    overallTotals: XhprofRawParentChildData
    bottlenecks?: 1 | 2 | 4 | 8 | 16 | 32 | 64 | 128 | 256
}

export interface XhprofNamespace {
    [key: string]: { excl_wt: number; group: XhprofCallCategory }
}

export interface XhprofCallCategory {
    namespace: string
}

export interface XhprofFlatInfo {
    overallTotals: XhprofRawParentChildData
    data: XhprofFlatInfoData
}

export interface XhprofFlatInfoData {
    [key: string]: XhprofCallInfo
}

export interface XhprofCallEdge {
    name: string
    ct: number
    pmu: number
    wt: number
}

export interface XhprofCallInfo {
    ct: number
    excl_ct: number
    wt: number
    excl_wt: number
    pmu?: number
    excl_pmu?: number
    perc_ct?: number
    perc_wt?: number
    perc_excl_wt?: number
    perc_pmu?: number
    perc_excl_pmu?: number
    cat?: string
    parents: { [parent: string]: XhprofCallEdge }
    children: { [child: string]: XhprofCallEdge }
    foldedNodes?: string[]
}

export const parseParentChild = (parentChild: string): string[] => {
    const parentChildReturn = parentChild.split('==>')
    // Return if both parent and child are set
    if (parentChildReturn[1]) {
        return parentChildReturn
    }

    return ['', parentChildReturn[0]]
}

const initializeCallInfoDefaults = (metrics: string[]): XhprofCallInfo => {
    const defaults = { parents: {}, children: {} }
    metrics.map(metric => {
        defaults[metric] = 0
        defaults['excl_' + metric] = 0
        if (metric !== 'ct') {
            defaults['perc_excl_' + metric] = 0
        }
        defaults['perc_' + metric] = 0
    })

    return defaults as XhprofCallInfo
}

export const computeFlatInfo = (rawData: XhprofRawData): XhprofFlatInfo => {
    const metrics = ['ct', 'wt']

    if (rawData['main()'] && Object.prototype.hasOwnProperty.call(rawData['main()'], 'pmu')) {
        metrics.push('pmu')
    }

    const overallTotals = rawData['main()']
    if (!overallTotals) {
        throw new Error('there is no data for node main()')
    }
    const data: XhprofFlatInfoData = {}
    Object.keys(rawData).forEach(parentChild => {
        const info = rawData[parentChild]

        const [parent, child] = parseParentChild(parentChild)

        if (parent === child) {
            return
        }

        if (!data[child]) {
            data[child] = initializeCallInfoDefaults(metrics)
        }

        metrics.forEach(metric => {
            data[child][metric] += info[metric]
            data[child]['excl_' + metric] += info[metric]
            if (info['perc_excl_' + metric]) {
                data[child]['perc_excl_' + metric] += info['perc_excl_' + metric]
            }
            if (info['perc_' + metric]) {
                data[child]['perc_' + metric] += info['perc_' + metric]
            }
            data[child].perc_wt = (data[child].wt / overallTotals.wt) * 100
            data[child].perc_excl_wt = (data[child].excl_wt / overallTotals.wt) * 100
        })
        overallTotals.ct += info.ct

        if (!parent) {
            return
        }

        if (!data[parent]) {
            data[parent] = initializeCallInfoDefaults(metrics)
        }

        data[child].parents[parent] = { ct: info.ct, wt: info.wt, pmu: info.pmu || 0, name: parent }
        data[parent].children[child] = { ct: info.ct, wt: info.wt, pmu: info.pmu || 0, name: child }

        metrics.forEach(metric => {
            data[parent]['excl_' + metric] -= info[metric]
            if (info['perc_excl_' + metric]) {
                data[parent]['perc_excl_' + metric] -= info['perc_excl_' + metric]
            }
            if (info['perc_' + metric]) {
                data[parent]['perc_' + metric] -= info['perc_' + metric]
            }
            data[parent].perc_wt = (data[parent].wt / overallTotals.wt) * 100
            data[parent].perc_excl_wt = (data[parent].excl_wt / overallTotals.wt) * 100
        })
    })

    // main() ct must be always 1, currently from rawdata gets >1, correcting here hardcoded
    if (data['main()']) {
        data['main()'].ct = 1
    }

    return { overallTotals, data }
}

export interface XhprofNearestFamilyResult {
    parents: {}
    children: {}
    ct_parents: number
    ct_children: number
}

export const computeXhprofNearestFamily = (rawData: XhprofRawData, functionName: string): XhprofNearestFamilyResult => {
    const result: XhprofNearestFamilyResult = { parents: {}, children: {}, ct_parents: 0, ct_children: 0 }
    Object.keys(rawData).map((parentChild, index) => {
        const [parent, child] = parseParentChild(parentChild)
        const info = rawData[parentChild]
        const posParent = parent.indexOf('#')
        const posChild = child.indexOf('#')
        if (posParent !== -1) {
            parent.substr(0, posParent)
        }
        if (posChild !== -1) {
            child.substr(0, posChild)
        }
        if (parent === functionName) {
            if (result.children[child]) {
                result.children[child].wt += info.wt
                result.children[child].ct += info.ct
            } else {
                result.children[child] = info
            }
            result.ct_children += info.ct
        }

        if (child === functionName && parent !== '') {
            if (result.parents[parent]) {
                result.parents[parent].wt += info.wt
                result.parents[parent].ct += info.ct
            } else {
                result.parents[parent] = info
            }
            result.ct_parents += info.ct
        }
    })
    return result
}

export interface XhprofRawParentChildData {
    wt: number
    ct: number
    excl_wt?: number
    excl_ct?: number
    pmu?: number
    perc_ct?: number
    perc_wt?: number
    perc_pmu?: number
}

export interface XhprofRawData {
    [key: string]: XhprofRawParentChildData
}

export const generateXhprofReport = (rawData: XhprofRawData): XhprofReport => {
    const { overallTotals, data } = computeFlatInfo(rawData)
    const layerReport: string[] = []
    let namespaces: XhprofNamespace[] = []
    Object.keys(data).map((key, index) => {
        const namespace = categorizeCall(key)
        const namespaceObj = {
            [namespace.namespace]: { excl_wt: data[key].excl_wt, group: namespace },
        }
        let pushOnce = true
        namespaces.map(value => {
            if (Object.prototype.hasOwnProperty.call(value, namespace.namespace)) {
                pushOnce = false
                value[namespace.namespace].excl_wt += data[key].excl_wt
            }
        })
        if (pushOnce) {
            namespaces.push(namespaceObj)
        }
    })
    namespaces.sort((a, b) => {
        const sortA = Object.keys(a)[0]
        const sortB = Object.keys(b)[0]
        if (a[sortA].excl_wt < b[sortB].excl_wt) {
            return 1
        }
        if (a[sortA].excl_wt > b[sortB].excl_wt) {
            return -1
        }
        return 0
    })
    namespaces = namespaces.slice(0, 10)
    const sortedData = sortXhprofGenericDataObject(data, 'excl_wt')
    return { data: sortedData, namespaces, layerReport, overallTotals }
}

export const categorizeCall = (call: string): XhprofCallCategory => {
    const uncategorized: XhprofCallCategory = { namespace: 'uncategorized' }

    const callName = new XhprofCall(call)

    if (!callName.namespaceName) {
        return uncategorized
    }

    // Don't categorize filename namespaces
    if (callName.namespaceName.startsWith('/')) {
        return uncategorized
    }

    for (const mapping of namespaceToPackageMappings) {
        if (callName.namespaceName.startsWith(mapping.identifier)) {
            return { namespace: mapping.package }
        }
    }

    const matchBundle = callName.namespaceName.match(/(([^\\]+)Bundle)/g)

    if (matchBundle && matchBundle.length > 0) {
        return { namespace: matchBundle[0] }
    }

    if (callName.namespaceName.indexOf('Shopware\\Plugins') !== -1) {
        const matchShopware = callName.namespaceName.split('\\')
        return { namespace: matchShopware[4] }
    }

    const firstBackslash = callName.namespaceName.indexOf('\\')
    if (firstBackslash !== -1) {
        return { namespace: callName.namespaceName.slice(0, firstBackslash) }
    }

    return { namespace: callName.namespaceName }
}
